import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useValueSummary, type ValueSummaryData } from '../hooks/useValueSummary';

const VALUE_ROWS = [
  {
    key: 'speedToShelf' as const,
    icon: 'rocket_launch',
    iconBg: 'var(--success-light)',
    iconColor: 'var(--success-dark)',
    titleKey: 'value.speedToShelf',
    metricsKey: 'value.speedMetrics',
    navigateTo: '/reports/activities',
    employeeNavigateTo: (clientId: string) => `/data-quality/${clientId}/reports/activities`,
  },
  {
    key: 'compliance' as const,
    icon: 'verified_user',
    iconBg: 'var(--secondary-light)',
    iconColor: 'var(--secondary)',
    titleKey: 'value.compliance',
    metricsKey: 'value.complianceMetrics',
    navigateTo: '/reports/compliance',
    employeeNavigateTo: (clientId: string) => `/data-quality/${clientId}/reports/compliance`,
  },
  {
    key: 'correctness' as const,
    icon: 'shield',
    iconBg: 'var(--primary-light)',
    iconColor: 'var(--primary)',
    titleKey: 'value.correctness',
    metricsKey: 'value.correctnessMetrics',
    navigateTo: '/reports/activities',
    employeeNavigateTo: (clientId: string) => `/data-quality/${clientId}/reports/activities`,
  },
] as const;

function getMetricsParams(key: string, data: ValueSummaryData, t: (key: string) => string): Record<string, string | number> {
  if (key === 'speedToShelf') {
    return {
      newProducts: data.speedToShelf.newProductsCount,
      leadTime: data.speedToShelf.avgLeadTimeDays ?? '-',
    };
  }
  if (key === 'compliance') {
    const ppwrLabel = data.compliance.ppwrStatus === 'compliant' ? t('value.ppwrCompliant')
      : data.compliance.ppwrStatus === 'pending' ? t('value.ppwrPending')
      : t('value.ppwrNotApplicable');
    return { gs1Updates: data.compliance.gs1UpdatesHandled, ppwrStatus: ppwrLabel };
  }
  return {
    managed: data.correctness.productsManagedCount ?? '-',
    blockers: data.correctness.openBlockersCount,
    questions: data.correctness.retailerQuestionsHandled,
  };
}

interface ValueSummaryProps {
  clientId: string;
  isEmployee?: boolean;
}

export function ValueSummary({ clientId, isEmployee }: ValueSummaryProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading } = useValueSummary(clientId);

  if (isLoading) {
    return (
      <div
        className="rounded-xl"
        style={{
          backgroundColor: 'var(--background)',
          border: '1px solid var(--border-soft)',
          boxShadow: 'var(--card-shadow)',
        }}
      >
        {VALUE_ROWS.map((row, i) => (
          <div
            key={row.key}
            className="flex items-center gap-3 px-4 py-3.5"
            style={{
              borderLeft: '3px solid var(--primary)',
              borderBottom: i < VALUE_ROWS.length - 1 ? '1px solid var(--border-soft)' : undefined,
            }}
          >
            <div className="skeleton h-9 w-9 rounded-lg" />
            <div className="flex-1 space-y-1.5">
              <div className="skeleton h-3 w-32 rounded" />
              <div className="skeleton h-2.5 w-48 rounded" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!data) return null;

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{
        backgroundColor: 'var(--background)',
        border: '1px solid var(--border-soft)',
        boxShadow: 'var(--card-shadow)',
      }}
    >
      {VALUE_ROWS.map((row, i) => {
        const path = isEmployee ? row.employeeNavigateTo(clientId) : row.navigateTo;
        const metricsParams = getMetricsParams(row.key, data, t);

        return (
          <div
            key={row.key}
            role="link"
            tabIndex={0}
            aria-label={t(row.titleKey)}
            className="value-row flex items-center gap-3 px-4 py-3.5"
            style={{
              borderLeft: '3px solid var(--primary)',
              borderBottom: i < VALUE_ROWS.length - 1 ? '1px solid var(--border-soft)' : undefined,
              cursor: 'pointer',
            }}
            onClick={() => navigate(path)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(path); } }}
          >
            <div
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
              style={{ backgroundColor: row.iconBg }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '18px', color: row.iconColor }}>
                {row.icon}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[0.8125rem] font-semibold" style={{ color: 'var(--foreground)' }}>
                {t(row.titleKey)}
              </p>
              <p className="text-xs truncate" style={{ color: 'var(--foreground-muted)' }}>
                {t(row.metricsKey, metricsParams)}
              </p>
            </div>
            <span className="material-symbols-outlined shrink-0" style={{ fontSize: '16px', color: 'var(--foreground-muted)' }}>
              chevron_right
            </span>
          </div>
        );
      })}

      <style>{`
        .value-row { transition: background-color 150ms ease; }
        .value-row:hover { background-color: var(--primary-light); }
      `}</style>
    </div>
  );
}
